import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  User, 
  MapPin, 
  Phone, 
  Video,
  Plus,
  Filter,
  Download,
  RefreshCw,
  X
} from 'lucide-react';
import { Card, Button, Badge, Select } from '../../components/ui';
import { generateMockAppointments, mockDoctors, mockServices } from '../../data/mockData';
import { formatDate, formatTime, getRelativeDate, getStatusColor } from '../../utils';

const PatientAppointments: React.FC = () => {
  const [appointments] = useState(generateMockAppointments());
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const statusOptions = [
    { value: 'all', label: 'All Appointments' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'past', label: 'Past' }
  ];

  // Filter appointments
  const filteredAppointments = appointments.filter(appointment => {
    const matchesStatus = filterStatus === 'all' || appointment.status === filterStatus;
    const isUpcoming = new Date(appointment.date) >= new Date();
    const matchesType = filterType === 'all' || 
      (filterType === 'upcoming' && isUpcoming) ||
      (filterType === 'past' && !isUpcoming);
    
    return matchesStatus && matchesType;
  });

  const getDoctor = (doctorId: string) => {
    return mockDoctors.find(d => d.id === doctorId);
  };

  const getService = (serviceId: string) => {
    return mockServices.find(s => s.id === serviceId);
  };

  const handleCancelAppointment = (appointmentId: string) => {
    // In a real app, this would make an API call
    console.log('Cancelling appointment:', appointmentId);
  };

  const handleRescheduleAppointment = (appointmentId: string) => {
    // In a real app, this would navigate to reschedule page
    console.log('Rescheduling appointment:', appointmentId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link to="/patient" className="text-blue-600 hover:text-blue-800">
                ← Back to Dashboard
              </Link>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link to="/booking">
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Book New Appointment
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            My Appointments
          </h1>
          <p className="text-gray-600">
            View and manage your upcoming and past appointments
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <Card className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <div className="flex items-center space-x-4">
                <Filter className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Filter by:</span>
                
                <Select
                  options={statusOptions}
                  value={filterStatus}
                  onChange={setFilterStatus}
                  className="min-w-40"
                />
                
                <Select
                  options={typeOptions}
                  value={filterType}
                  onChange={setFilterType}
                  className="min-w-32"
                />
              </div>
              
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <span>{filteredAppointments.length} appointments</span>
                <Button variant="ghost" size="sm">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Appointments List */}
        <div className="space-y-4">
          {filteredAppointments.length > 0 ? (
            filteredAppointments.map((appointment) => {
              const doctor = getDoctor(appointment.doctorId);
              const service = getService(appointment.serviceId);
              const isUpcoming = new Date(appointment.date) >= new Date();
              
              return (
                <Card key={appointment.id} className="p-6 hover:shadow-md transition-shadow">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                    {/* Appointment Info */}
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Calendar className="w-8 h-8 text-blue-600" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {service?.name || 'General Consultation'}
                            </h3>
                            <Badge 
                              variant={appointment.status === 'confirmed' ? 'success' : 
                                     appointment.status === 'cancelled' ? 'danger' : 'primary'}
                            >
                              {appointment.status}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <div className="flex items-center space-x-1">
                              <User className="w-4 h-4" />
                              <span>{doctor?.name || 'Dr. Smith'}</span>
                            </div>
                            
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>{getRelativeDate(appointment.date)}</span>
                            </div>
                            
                            <div className="flex items-center space-x-1">
                              <Clock className="w-4 h-4" />
                              <span>{formatTime(appointment.time)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Additional Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">123 Medical Plaza, Suite 201</span>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">+1 (555) 123-4567</span>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">Duration: {appointment.duration} minutes</span>
                          </div>
                          
                          {appointment.notes && (
                            <div className="text-gray-600">
                              <span className="font-medium">Notes:</span> {appointment.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col space-y-2 lg:w-48">
                      {isUpcoming && appointment.status !== 'cancelled' && (
                        <>
                          <Button variant="outline" size="sm" className="w-full">
                            <Video className="w-4 h-4 mr-2" />
                            Join Video Call
                          </Button>
                          
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full"
                            onClick={() => handleRescheduleAppointment(appointment.id)}
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Reschedule
                          </Button>
                          
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="w-full text-red-600 hover:text-red-800"
                            onClick={() => handleCancelAppointment(appointment.id)}
                          >
                            <X className="w-4 h-4 mr-2" />
                            Cancel
                          </Button>
                        </>
                      )}
                      
                      {!isUpcoming && appointment.status === 'completed' && (
                        <>
                          <Button variant="outline" size="sm" className="w-full">
                            <Download className="w-4 h-4 mr-2" />
                            Visit Summary
                          </Button>
                          
                          <Button variant="outline" size="sm" className="w-full">
                            <Plus className="w-4 h-4 mr-2" />
                            Book Follow-up
                          </Button>
                        </>
                      )}
                      
                      {appointment.status === 'cancelled' && (
                        <Button variant="outline" size="sm" className="w-full">
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Book Again
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })
          ) : (
            <Card className="p-12 text-center">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No appointments found
              </h3>
              <p className="text-gray-600 mb-6">
                {filterStatus === 'all' && filterType === 'all' 
                  ? "You don't have any appointments yet."
                  : "No appointments match your current filters."
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/booking">
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Book Your First Appointment
                  </Button>
                </Link>
                {(filterStatus !== 'all' || filterType !== 'all') && (
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setFilterStatus('all');
                      setFilterType('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Help Section */}
        <Card className="mt-8 p-6 bg-blue-50 border-blue-200">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Phone className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Need Help with Your Appointment?
              </h3>
              <p className="text-blue-800 mb-4">
                Our patient care team is here to help you with scheduling, rescheduling, 
                or any questions about your upcoming visits.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Patient Services
                </Button>
                <Link to="/contact">
                  <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                    Contact Us Online
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PatientAppointments;